import { type RefObject, useEffect } from "react";

/**
 * A React hook that enables submitting a form or triggering a callback
 * when the user presses Cmd + Enter (⌘ + Enter) while focused on a textarea,
 * but only on macOS devices.
 *
 * @param ref - React ref object attached to the textarea element
 * @param callback - Function to call when Cmd + Enter is pressed
 */
export function useCmdEnterSubmit(ref: RefObject<HTMLTextAreaElement>, callback: () => void): void {
  useEffect(() => {
    const textarea = ref.current;
    if (!isMacOS() || !textarea) return;

    const controller = new AbortController();

    function handleKeyDown(event: KeyboardEvent) {
      if (event.metaKey && event.key == "Enter") {
        event.preventDefault();
        callback();
      }
    }

    textarea.addEventListener("keydown", handleKeyDown, { signal: controller.signal });

    return () => {
      controller.abort();
    };
  }, [ref, callback]);
}

function isMacOS(): boolean {
  return (
    typeof window.navigator != "undefined" &&
    window.navigator.platform.toUpperCase().includes("MAC")
  );
}
